import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box, CircularProgress, Grid, Paper, Tab, Tabs, Typography,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import moment from 'moment';
import { getFamilyMemberDataAPI } from '../../services/mainService';
import HealthParameters from '../../components/QRcodeComponent/HealthParameters';
import PatientDetails from '../../components/QRcodeComponent/PatientDetails';
import MedicalHistory from '../../components/QRcodeComponent/MedicalHistory';
import { useStyles } from '../../assets/styles/PatientDashboardStyles';
import { useStyles as healthParamStyle } from '../../assets/styles/PatientDashboardStyles';
import { usePatientHealthStyles } from '../../components/ui/Patient/style';
import { useHealthParameterStyles } from '../../components/ui/HealthParameters/style';
import { HealthParameterList } from '../../constant/staticLists';
import AppLayout from '../SideBar';

const Container = styled(Grid)({
  '& .MuiTypography-root': {
    fontFamily: 'Poppins',
    fontStyle: 'normal',
  },
  '& .pagetitle': {
    paddingTop: 70,
    '& .MuiTypography-root': {
      fontWeight: 500,
      fontSize: '32px',
      lineHeight: '42px',
      color: '#2B2B2B',
      '& + .MuiTypography-root': {
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '18px',
        color: '#5E5E5E',
      },
    },
  },
});

const StyledTabs = styled(Tabs)({
  borderBottom: '1.2px solid #F5F5F7',
  '& .MuiTab-root': {
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '18px',
    color: '#5E5E5E',
  },
  '& .Mui-selected': {
    background: 'linear-gradient(90deg, #097EB7 0%, #2AAB6C 100%)',
    backgroundClip: 'text',
    textFillColor: 'transparent',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    borderBottom: '1.2px solid',
  },
});

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const FamilyMemberData = () => {
  const history = useHistory();
  const { memberId } = useParams();
  const commonClasses = useStyles();
  const patientHealthClasses = usePatientHealthStyles();
  const healthParamClasses = useHealthParameterStyles();
  const newClasses = { ...commonClasses, ...patientHealthClasses, ...healthParamClasses };

  const { userData } = useSelector((state) => ({ userData: state.auth.userData }));
  const token = userData?.token;

  const [tab, setTab] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [duration, setDuration] = useState(
    HealthParameterList.reduce((acc, cur) => { acc[cur.primaryKey] = 'w'; return acc; }, {})
  );
  const [date, setDate] = useState(
    HealthParameterList.reduce((acc, cur) => {
      acc[cur.primaryKey] = moment(new Date()).format('YYYY-MM-DD');
      return acc;
    }, {})
  );

  const prevDur = useRef(duration);
  const prevDate = useRef(date);

  const fetchData = async (opts = {}) => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const dur = (opts.duration || Object.values(duration)[0] || 'w').toLowerCase();
    const dt = opts.date || Object.values(date)[0] || moment().format('YYYY-MM-DD');
    setLoading(true);
    const res = await getFamilyMemberDataAPI({ token, memberId, timezone, duration: dur, date: dt });
    setLoading(false);
    if (res.response_type === 'fail') {
      toast.error(res.response?.message || 'Failed to load data.', { position: 'bottom-center', closeButton: false });
    } else {
      setData(res.response.data);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const preValues = Object.entries(prevDur.current);
    const values = Object.entries(duration);
    let changed;
    preValues.forEach((el, i) => { if (el[1] !== values[i][1]) changed = values[i]; });
    prevDur.current = duration;
    if (changed) fetchData({ duration: changed[1] });
  }, [duration]);

  useEffect(() => {
    const preValues = Object.entries(prevDate.current);
    const values = Object.entries(date);
    let changed;
    preValues.forEach((el, i) => { if (el[1] !== values[i][1]) changed = values[i]; });
    prevDate.current = date;
    if (changed) fetchData({ date: changed[1] });
  }, [date]);

  return (
    <Grid container spacing={0}>
      <AppLayout>
        <Container container spacing={4}>
          <Grid item xs={12} className="pagetitle">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                onClick={() => history.push('/patient/family-members')}
                sx={{
                  display: 'flex', alignItems: 'center', cursor: 'pointer',
                  color: '#097EB7', mr: 1,
                }}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography>Medical Details</Typography>
                <Typography>Viewing health data of your family member.</Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={0} sx={{ paddingBottom: '20px' }}>
              <Paper elevation={1} sx={{ width: '100%' }}>
                <Grid item xs={12}>
                  <StyledTabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="scrollable"
                    TabIndicatorProps={{ style: { display: 'none' } }}
                  >
                    {['Patient Details', 'Medical History', 'Health Parameters'].map((label, i) => (
                      <Tab key={i} label={label} id={`fmd-tab-${i}`} aria-controls={`fmd-tabpanel-${i}`} />
                    ))}
                  </StyledTabs>
                </Grid>

                <Grid item xs={12} sx={{ paddingLeft: '16px' }}>
                  <TabPanel value={tab} index={0}>
                    {!data ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                        <CircularProgress size={50} thickness={5} />
                      </Box>
                    ) : (
                      <PatientDetails data={data.PersonalDetails} />
                    )}
                  </TabPanel>

                  <TabPanel value={tab} index={1}>
                    <MedicalHistory data={data?.MedicalHistory} />
                  </TabPanel>

                  <TabPanel value={tab} index={2}>
                    <Box className={newClasses.mainHealthCont} sx={{ position: 'relative' }}>
                      <Grid container justify="center" className={newClasses.secondaryMainHealth}>
                        {data && (
                          <HealthParameters
                            className={newClasses}
                            list={HealthParameterList}
                            duration={duration}
                            setDuration={setDuration}
                            date={date}
                            setDate={setDate}
                            isHideModalPop
                            healthParam={data.HealthParameters}
                            setQRData={setData}
                            token={null}
                            isLoading={loading}
                          />
                        )}
                      </Grid>
                      {loading && (
                        <Box sx={{
                          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                          backgroundColor: 'rgba(255,255,255,0.8)', display: 'flex',
                          justifyContent: 'center', alignItems: 'center', zIndex: 9999, minHeight: 400,
                        }}>
                          <CircularProgress size={50} thickness={4} sx={{ color: '#2AAB6C' }} />
                        </Box>
                      )}
                    </Box>
                  </TabPanel>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </AppLayout>
    </Grid>
  );
};

export default FamilyMemberData;
